import { useState } from "react"
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from "./ui/Button"
import { Card, CardContent } from "./ui/Card"
import { useTheme } from "./ThemeContext"

export default function ProjectsSkills() {
  const [isExpanded, setIsExpanded] = useState(true)
  const { theme } = useTheme()

  const projects = [
    {
      date: "March 2025",
      title: "harshvardhan.us",
      tech: "HTML | CSS | JavaScript | AJAX | C# | ASP.NET | Bootstrap | MVC",
      description:
        "Created a personal portfolio website using the ASP.NET Core MVC framework with a focus on responsive web design and performance. Features include dark mode toggling, a responsive background and picture based on mouse position, scroll animations, and hover highlighting.",
    },
    {
      date: "October 2024",
      title: "Online Examination System",
      tech: "HTML | CSS | JavaScript | MySQL | AJAX | JSON",
      description:
        "Crafted a bespoke interface for student and teacher logins, employing HTML, CSS, and JavaScript, backed by Xampp Server, PHP, andMySQL. • Demonstrated web development expertise by seamlessly integrating front-end design andback-endfunctionality, improving user experience and system efficiency",
    },
    {
      date: "September 2023",
      title: "Number guessing Game",
      tech: "Python | Tinker | PyGame",
      description:
        "Designed and implemented a user-friendly number guessing game interface using Python andPygame. • Enhanced user engagement with captivating game play mechanics, offering an enjoyable solitary experience.",
    },
  ]

  const skills = [
    { name: "Python", icon: "/images/skills/python-icon.png" },
    { name: "C#", icon: "/images/skills/csharp-icon.png" },
    { name: "JavaScript", icon: "/images/skills/js-icon.png" },
    { name: "HTML5", icon: "/images/skills/html-icon.png" },
    { name: "CSS3", icon: "/images/skills/css-icon.png" },
    { name: "JSON", icon: "/images/skills/json-icon.png" },
  ]

  return (
    <section
      id="projects"
      className={`py-16 transition-colors duration-300 ${theme === "dark" ? "bg-gray-900" : "bg-white"}`}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2
            className={`text-4xl font-bold mb-4 transition-colors duration-300 ${
              theme === "dark" ? "text-white" : "text-gray-800"
            }`}
          >
            Projects & Skills
          </h2>
          <Button
            variant="ghost"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-blue-500 hover:text-blue-700"
          >
            Collapse {isExpanded ? <ChevronUp className="ml-2 h-4 w-4" /> : <ChevronDown className="ml-2 h-4 w-4" />}
          </Button>
        </div>

        {isExpanded && (
          <div className="space-y-16">
            {/* Projects */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project, index) => (
                <Card
                  key={index}
                  className={`overflow-hidden hover:shadow-xl hover:border-2 hover:border-yellow-400 transition-all duration-300 cursor-pointer ${
                    theme === "dark"
                      ? "bg-gray-800 border-gray-700 hover:bg-gray-700"
                      : "bg-white border-gray-200 hover:shadow-lg"
                  }`}
                >
                  <CardContent className="p-6 h-full flex flex-col">
                    <div className="flex-1">
                      <div className="text-blue-500 text-sm mb-2">{project.date}</div>
                      <h3
                        className={`text-xl font-bold mb-3 transition-colors duration-300 ${
                          theme === "dark" ? "text-white" : "text-gray-800"
                        }`}
                      >
                        {project.title}
                      </h3>
                      <div className="text-yellow-600 font-semibold text-sm mb-4">{project.tech}</div>
                      <p
                        className={`text-sm leading-relaxed transition-colors duration-300 ${
                          theme === "dark" ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        {project.description}
                      </p>
                    </div>
                    <div className="mt-6">
                      <div className="relative h-16 -mx-6 -mb-6 flex items-center justify-center overflow-hidden rounded-b-lg">
                        <img
                          src="/images/project-bottom-wave.png"
                          alt="Wave Background"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <Button
                          onClick={() => window.open("https://github.com/harsh-vardhan1/", "_blank")}
                          variant="outline"
                          className="relative z-10 bg-transparent border-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black font-semibold px-6 py-2 rounded-lg transition-all duration-300"
                        >
                          View on GitHub
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Technical Skills */}
            <div>
              <h3
                className={`text-3xl font-bold text-center mb-8 transition-colors duration-300 ${
                  theme === "dark" ? "text-white" : "text-gray-800"
                }`}
              >
                Technical Skills
              </h3>
              <div className="flex flex-wrap justify-center gap-8">
                {skills.map((skill, index) => (
                  <div key={index} className="flex flex-col items-center group cursor-pointer">
                    <div className="w-16 h-16 flex items-center justify-center mb-2 transition-transform duration-300 group-hover:scale-110">
                      <img
                        src={skill.icon || "/placeholder.svg"}
                        alt={`${skill.name} Icon`}
                        width={64}
                        height={64}
                        className="object-contain"
                      />
                    </div>
                    <span
                      className={`text-sm transition-colors duration-300 ${
                        theme === "dark" ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {skill.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}